import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const VIEW_AS_STORAGE_KEY = "rf_read_only_view";
let verifiedViewAsToken = null;
let verifiedViewAsMode = null;
const getViewAsSession = () => {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const getViewAsSessionState = getViewAsSession;
export const isVerifiedViewAsSession = (token, mode) =>
  Boolean(token && verifiedViewAsToken === token && verifiedViewAsMode === mode);
export const markViewAsSessionVerified = (token, mode = "read_only") => {
  verifiedViewAsToken = token || null;
  verifiedViewAsMode = token ? mode : null;
};
export const canStoreViewAsSession = () => {
  if (typeof window === "undefined") return false;
  try {
    const key = `${VIEW_AS_STORAGE_KEY}_test`;
    window.sessionStorage.setItem(key, "1");
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};
export const setViewAsSession = (token, viewAs) => {
  if (typeof window === "undefined") return false;
  try {
    window.sessionStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify({ token, viewAs }));
    return true;
  } catch {
    return false;
  }
};
export const clearViewAsSession = () => {
  verifiedViewAsToken = null;
  verifiedViewAsMode = null;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch { /* ignore storage cleanup errors */ }
};

let _getToken = null;
export const setTokenGetter = (fn) => { _getToken = fn; };
export const getCurrentAuthToken = async () => {
  if (!_getToken) return null;
  try { return await _getToken(); } catch { return null; }
};

api.interceptors.request.use(async (config) => {
  const viewSession = getViewAsSession();
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  // Public bootstrap runs before Clerk installs the token getter. A tab-local
  // view token here would require the very authentication we're bootstrapping.
  if (method === "get" && url.split("?")[0] === "/config") return config;
  const isViewExit = /\/auth\/view-as\/exit\/?$/.test(url);
  const isLogout = /\/auth\/logout\/?$/.test(url);
  if (viewSession?.token) {
    config.headers = config.headers || {};
    config.headers["X-Read-Only-View-Token"] = viewSession.token;
    const isVerifiedActAs =
      viewSession.viewAs?.mode === "act_as" &&
      isVerifiedViewAsSession(viewSession.token, viewSession.viewAs.mode);
    if (!["get", "head", "options"].includes(method) &&
        !isViewExit && !isLogout && !isVerifiedActAs) {
      return Promise.reject(new Error("Changes are disabled until an act-as session is verified."));
    }
  }
  if (_getToken) {
    try {
      const token = await _getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {}
  }
  return config;
});

export default api;
